// src/pages/mallkiosk/CartReviewScreen.js
// Items grouped by vendor → create real per-vendor orders via the session
// checkout flow (reused from MultiVendorCheckout.js) → PaymentModal for the
// combined total. Do not reinvent the cart-split-by-vendor / order-before-
// payment logic — it already fixed the "Order not found" bug elsewhere.
// FUTURE: upsell step ("Add a drink?") insertion point is marked below.
import React, { useState } from 'react';
import { useLanguage }      from '../../context/LanguageContext';
import { useMultiCart }     from '../../context/MultiCartContext';
import PaymentModal         from '../../components/PaymentModal';
import { calculateServiceFee } from '../../utils/serviceFeeCalculator';

export default function CartReviewScreen({ mall, vendors, carts, totals, onBack, onAddMore, onCancelOrder, placeOrders, onOrdersPlaced, onSuccess }) {
  const { t, isAr } = useLanguage();
  const { setQty, removeItem } = useMultiCart();

  const [tableNo,        setTableNo]        = useState('');
  const [customerPhone,  setCustomerPhone]  = useState('');
  const [placing,        setPlacing]        = useState(false);
  const [placeError,     setPlaceError]     = useState('');
  const [showPayment,    setShowPayment]    = useState(false);
  const [paymentOrder,   setPaymentOrder]   = useState(null);
  const [createdOrders,  setCreatedOrders]  = useState([]);

  const vendorList = vendors
    .map(vd => vd.vendor)
    .filter(v => (carts[v.id] || []).length > 0);

  const combinedSubtotal = vendorList.reduce((s, v) => s + (totals.byVendor[v.id]?.subtotal || 0), 0);
  const combinedTax      = vendorList.reduce((s, v) => s + (totals.byVendor[v.id]?.tax || 0), 0);

  // Preview service fee from mall config
  const mallFeeConfig = mall?.serviceFeeConfig
    ? (typeof mall.serviceFeeConfig === 'string' ? (() => { try { return JSON.parse(mall.serviceFeeConfig); } catch(_) { return null; } })() : mall.serviceFeeConfig)
    : null;
  const previewFee   = calculateServiceFee(combinedSubtotal, mallFeeConfig);

  const [serverFee, setServerFee] = useState(null); // set from server response after orders placed
  const displayFee   = serverFee || previewFee;
  const displayTotal = parseFloat((totals.grandTotal + displayFee.amount).toFixed(2));

  const handleProceed = async () => {
    if (placing || vendorList.length === 0) return;
    setPlacing(true); setPlaceError('');
    try {
      // placeOrders returns { orders, serviceFee, paymentTotal, transactionGroupId }
      const result = await placeOrders(customerPhone.trim());
      const orders = Array.isArray(result) ? result : (result.orders || []);
      const fee    = result.serviceFee || { amount: 0, label: 'Service Fee' };
      const paymentTotal = result.paymentTotal ?? (totals.grandTotal + fee.amount);

      setServerFee(fee);
      setCreatedOrders(orders);
      onOrdersPlaced(orders);

      const combinedItems = vendorList.flatMap(v =>
        (carts[v.id] || []).map(i => ({ name: `[${v.name}] ${i.name}`, quantity: i.qty, price: i.price }))
      );
      setPaymentOrder({
        id:               orders[0]?.id,
        orderNumber:      orders.length === 1 ? orders[0].orderNumber : orders.map(o => o.orderNumber).join(' · '),
        total:            paymentTotal,
        subtotal:         parseFloat(combinedSubtotal.toFixed(2)),
        tax:              parseFloat(combinedTax.toFixed(2)),
        serviceFeeAmount: fee.amount,
        serviceFeeLabel:  fee.label,
        tableNo:          tableNo || undefined,
        items:            combinedItems,
        vendorId:         orders[0]?.vendorId,
      });
      setShowPayment(true);
    } catch (err) {
      setPlaceError(err.response?.data?.error || err.message || 'Could not place orders. Please try again.');
    } finally {
      setPlacing(false);
    }
  };

  const handlePaymentSuccess = () => {
    setShowPayment(false);
    onSuccess();
  };

  const handlePaymentClose = () => {
    setShowPayment(false);
    // Orders already exist as AWAITING_PAYMENT — staff can cancel from Orders tab if abandoned.
  };

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg)', direction: isAr ? 'rtl' : 'ltr' }}>

      {/* Header */}
      <div style={{ padding: '24px 40px 12px', display: 'flex', alignItems: 'center', gap: 18 }}>
        <button onClick={onBack} style={{
          width: 56, height: 56, borderRadius: 16, background: 'var(--bg3)', border: '1px solid var(--border)',
          color: 'var(--text)', fontSize: 26, cursor: 'pointer',
        }}>{isAr ? '→' : '←'}</button>
        <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--text)' }}>{t('Your Order', 'طلبك')}</div>
        <button onClick={onCancelOrder} style={{
          marginInlineStart: 'auto', padding: '12px 22px', borderRadius: 14, background: 'var(--bg3)',
          border: '1px solid var(--border)', color: 'var(--text2)', fontSize: 15, fontWeight: 600, cursor: 'pointer',
        }}>
          {t('✕ Start Over', '✕ البدء من جديد')}
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 40px 24px', maxWidth: 900, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>

        {placeError && (
          <div style={{ padding: '14px 18px', background: '#3d1a1a', borderRadius: 12, marginBottom: 16, fontSize: 16, color: 'var(--red)', border: '1px solid var(--red)' }}>
            ❌ {placeError}
          </div>
        )}

        {vendorList.length === 0 && (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)', fontSize: 20 }}>{t('Your cart is empty', 'سلتك فارغة')}</div>
        )}

        {vendorList.map(v => (
          <div key={v.id} style={{ marginBottom: 26 }}>
            <div style={{ fontWeight: 800, fontSize: 22, marginBottom: 10, color: 'var(--text)' }}>
              {v.emoji} {v.name}
            </div>
            {(carts[v.id] || []).map(item => (
              <div key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--text)' }}>{item.name}</div>
                  {item.bundleComponentsDetail?.length > 0 ? (
                    <div style={{ marginTop: 4 }}>
                      {item.bundleComponentsDetail.map((c, ci) => (
                        <div key={ci} style={{ fontSize: 13, color: 'var(--text3)', paddingInlineStart: 14 }}>
                          • {c.componentName}: {c.modifierNote || t('No customization', 'بدون تخصيص')}
                        </div>
                      ))}
                    </div>
                  ) : item.modifierNote && (
                    <div style={{ fontSize: 13, color: 'var(--text3)' }}>{item.modifierNote}</div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button onClick={() => setQty(v.id, item.id, item.qty - 1)} style={{
                      width: 40, height: 40, borderRadius: '50%', background: 'var(--bg3)', border: '1px solid var(--border)',
                      color: 'var(--text)', fontSize: 20, cursor: 'pointer',
                    }}>−</button>
                    <span style={{ fontSize: 18, fontWeight: 700, minWidth: 24, textAlign: 'center', color: 'var(--text)' }}>{item.qty}</span>
                    <button onClick={() => setQty(v.id, item.id, item.qty + 1)} style={{
                      width: 40, height: 40, borderRadius: '50%', background: 'var(--accent)', border: 'none',
                      color: '#000', fontSize: 20, cursor: 'pointer',
                    }}>+</button>
                  </div>
                  <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--accent)', minWidth: 90, textAlign: 'right' }}>
                    AED {(item.price * item.qty).toFixed(2)}
                  </span>
                  <button onClick={() => removeItem(v.id, item.id)} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 22, cursor: 'pointer' }}>✕</button>
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, color: 'var(--text2)', marginTop: 8 }}>
              <span>{t('Subtotal', 'الإجمالي الفرعي')}</span>
              <span>AED {(totals.byVendor[v.id]?.subtotal || 0).toFixed(2)}</span>
            </div>
          </div>
        ))}

        {/* FUTURE: upsell step ("Add a drink?") would go here, before the summary */}

        {vendorList.length > 0 && (
          <>
            <div style={{ marginBottom: 16 }}>
              <input type="text" value={tableNo} onChange={e => setTableNo(e.target.value)}
                placeholder={t('Table number (optional)', 'رقم الطاولة (اختياري)')}
                style={{ width: '100%', boxSizing: 'border-box', padding: '16px 18px', fontSize: 18, borderRadius: 12 }} />
            </div>
            <div style={{ borderTop: '2px solid var(--border)', paddingTop: 16, marginTop: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 17, color: 'var(--text2)', marginBottom: 8 }}>
                <span>{t('Subtotal', 'الإجمالي الفرعي')}</span>
                <span>AED {combinedSubtotal.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 17, color: 'var(--text2)', marginBottom: 8 }}>
                <span>{t('VAT (5%)', 'ضريبة القيمة المضافة (5%)')}</span>
                <span>AED {combinedTax.toFixed(2)}</span>
              </div>
              {displayFee.amount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 17, color: 'var(--text2)', marginBottom: 8 }}>
                  <span>{displayFee.label || t('Service Fee', 'رسوم الخدمة')}</span>
                  <span>AED {displayFee.amount.toFixed(2)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 28, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                <span style={{ color: 'var(--text)' }}>{t('Total', 'الإجمالي')}</span>
                <span style={{ color: 'var(--accent)' }}>AED {displayTotal.toFixed(2)}</span>
              </div>
              <div style={{ fontSize: 15, color: 'var(--text2)', marginTop: 6 }}>
                {totals.totalItems} {t('items from', 'عناصر من')} {vendorList.length} {t('stalls', 'متاجر')}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 16, marginTop: 24 }}>
              <button onClick={onAddMore} style={{
                flex: 1, padding: 20, borderRadius: 16, background: 'var(--bg3)', border: '1px solid var(--border)',
                color: 'var(--text)', fontSize: 18, fontWeight: 700, cursor: 'pointer',
              }}>← {t('Add More', 'إضافة المزيد')}</button>
              <button onClick={handleProceed} disabled={placing} style={{
                flex: 2, padding: 20, borderRadius: 16, background: 'var(--accent)', border: 'none',
                color: '#000', fontSize: 20, fontWeight: 800, cursor: placing ? 'default' : 'pointer', opacity: placing ? .7 : 1,
              }}>
                {placing ? t('⏳ Placing orders...', '⏳ جارٍ تقديم الطلب...') : `${t('Proceed to Payment', 'المتابعة للدفع')} →`}
              </button>
            </div>
          </>
        )}
      </div>

      {showPayment && paymentOrder && (
        <PaymentModal
          order={paymentOrder}
          vendorId={paymentOrder.vendorId}
          paymentConfig={mall?.paymentConfig}
          sourceKiosk="MALL_KIOSK"
          onSuccess={handlePaymentSuccess}
          onClose={handlePaymentClose}
        />
      )}
    </div>
  );
}
